<script setup lang="ts">
import { useField } from 'vee-validate';
import type { VeeImageFieldProps } from '../types';
import { ref, toRef, computed } from 'vue';
import ImageField from './ImageField.vue';
import { UploadProgress } from '@/utils/useFileUpload';
import { useVeeFormProps } from '../utils';
import { useUploadValidation } from '@/utils/useUploadValidation';

const props = defineProps<VeeImageFieldProps>();

const veeProps = useVeeFormProps(props);

const { value, handleChange, errorMessage } = useField<string | undefined>(
  toRef(() => props.name),
  undefined,
  { validateOnValueUpdate: false },
);

const onUpdate = (input: string | undefined) => {
  handleChange(input, false);
};

const currentUploadProgress = ref<UploadProgress>();

const onUploadProgressUpdate = (uploadProgress: UploadProgress) => {
  currentUploadProgress.value = uploadProgress;
};

const isUploadRunning = computed(
  () =>
    currentUploadProgress.value?.state === 'pending' ||
    currentUploadProgress.value?.state === 'idle',
);

const isUploadError = computed(() => Boolean(currentUploadProgress.value?.state === 'failed'));

useUploadValidation({
  isUploadRunning,
  isUploadError,
  name: toRef(() => props.name),
});
</script>

<template>
  <ImageField
    v-bind="veeProps"
    :modelValue="value"
    :errorMessage="errorMessage"
    @update:modelValue="onUpdate"
    @update:uploadProgress="onUploadProgressUpdate"
  />
</template>
